import React, { useState } from "react";

interface CountryInfo {
    name: {
        common: string;
        official: string;
    };
    capital: string[];
    languages: { [key: string]: string };
    currencies: { [key: string]: { name: string; symbol: string } };
    flags: {
        png: string;
        svg: string;
    };
    population: number;
    region: string;
    cca2: string; // Alpha-2 code
    cca3: string; // Alpha-3 code
}

const defaultResults = [
    { label: "Common Name", value: "—" },
    { label: "Official Name", value: "—" },
    { label: "Alpha-2 Code", value: "—" },
    { label: "Alpha-3 Code", value: "—" },
    { label: "Capital", value: "—" },
    { label: "Region", value: "—" },
    { label: "Population", value: "—" },
    { label: "Languages", value: "—" },
    { label: "Currencies", value: "—" }
];

function CountryCodeConverter() {
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [results, setResults] = useState(defaultResults);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [flag, setFlag] = useState<string | null>(null);

    const fetchCountryInfo = async () => {
        if (!searchQuery.trim()) {
            setError("Please enter a country name or code");
            setResults(defaultResults);
            setFlag(null);
            return;
        }

        setLoading(true);
        setError("");
        
        try {
            const query = searchQuery.trim();
            let endpoint = "";

            // Determine the appropriate endpoint based on input length and type
            if (query.length === 2) {
                endpoint = `alpha/${query.toUpperCase()}`; // Alpha-2 search
            } else if (query.length === 3) {
                endpoint = `alpha/${query.toUpperCase()}`; // Alpha-3 search
            } else {
                // For name search, we need to handle it differently
                endpoint = `name/${encodeURIComponent(query)}`;
            }

            const response = await fetch(`https://restcountries.com/v3.1/${endpoint}`);
            
            if (!response.ok) {
                throw new Error("Country not found");
            }

            const data = await response.json();
            
            if (data.length === 0) {
                throw new Error("Country not found");
            }

            // For name search, find the exact match if possible
            const countryInfo = query.length !== 2 && query.length !== 3
                ? data.find((country: CountryInfo) => 
                    country.name.common.toLowerCase() === query.toLowerCase()
                  ) || data[0]
                : data[0];

            setFlag(countryInfo.flags.svg);
            setResults([
                { label: "Common Name", value: countryInfo.name.common },
                { label: "Official Name", value: countryInfo.name.official },
                { label: "Alpha-2 Code", value: countryInfo.cca2 },
                { label: "Alpha-3 Code", value: countryInfo.cca3 },
                { label: "Capital", value: countryInfo.capital?.join(", ") || "N/A" },
                { label: "Region", value: countryInfo.region },
                { label: "Population", value: formatNumber(countryInfo.population) },
                { label: "Languages", value: countryInfo.languages ? formatLanguages(countryInfo.languages) : "N/A" },
                { label: "Currencies", value: countryInfo.currencies ? formatCurrencies(countryInfo.currencies) : "N/A" }
            ]);
        } catch (err) {
            setError("Country not found. Please try a different name or code");
            setResults(defaultResults);
            setFlag(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            fetchCountryInfo();
        }
    };

    const formatLanguages = (languages: { [key: string]: string }) => {
        return Object.values(languages).join(", ");
    };

    const formatCurrencies = (currencies: { [key: string]: { name: string; symbol: string } }) => {
        return Object.entries(currencies)
            .map(([code, { name, symbol }]) => `${name} (${symbol}) [${code}]`)
            .join(", ");
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat().format(num);
    };

    return (
        <div className="tool">
            <h2>Country Code Converter</h2>
            <div className="content">
                <div className="country-converter">
                    <div className="country-converter__input-section">
                        <div className="country-converter__input-container">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter country name, Alpha-2, or Alpha-3 code (e.g., Spain, ES, ESP)"
                                className="country-converter__input"
                            />
                            <button 
                                onClick={() => setSearchQuery("")}
                                className="country-converter__clear-button"
                            >
                                Clear
                            </button>
                        </div>

                        <button 
                            onClick={fetchCountryInfo}
                            className="country-converter__convert-button"
                            disabled={loading}
                        >
                            {loading ? "Loading..." : "Search"}
                        </button>
                    </div>

                    <div className="country-converter__results">
                        {error && <p className="error-message">{error}</p>}
                        {flag && (
                            <div className="country-converter__flag-container">
                                <img 
                                    src={flag} 
                                    alt="Country flag" 
                                    className="country-converter__flag"
                                />
                            </div>
                        )}
                        {results.map((result, index) => (
                            <div key={index} className="country-converter__result-item">
                                <strong>{result.label}:</strong> {result.value}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CountryCodeConverter;
